import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, Badge } from "@b2b/ui-components";
import { AlertCircle } from "lucide-react";
import { sellerService } from "@b2b/services";
import { Link } from "react-router-dom";

interface PendingProduct {
  id: string;
  name: string;
  categoryName?: string;
  approvalStatus?: string;
  status?: string;
  createdAt?: string;
  images?: string[];
}

export const PendingActions = () => {
  const [products, setProducts] = useState<PendingProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPendingProducts = async () => {
      try {
        setLoading(true);
        const response = await sellerService.getSellerProducts({
          approvalStatus: "pending",
          page: 1,
          limit: 20,
        });

        const apiProducts = Array.isArray(response?.data?.products)
          ? response.data.products
          : [];

        setProducts(apiProducts);
      } catch (error) {
        console.error("Error fetching pending products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingProducts();
  }, []);

  const pendingProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          String(product.approvalStatus || "").toLowerCase() === "pending"
      ),
    [products]
  );

  const displayedPendingProducts = useMemo(
    () => pendingProducts,
    [pendingProducts]
  );

  if (loading) {
    return (
      <Card className="bg-white border shadow-sm h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            Products Pending Approval
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-3 rounded-lg border">
                <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-64 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (displayedPendingProducts.length === 0) {
    return (
      <Card className="bg-white border shadow-sm h-full flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            Products Pending Approval
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 overflow-y-auto">
          <div className="text-center py-8 text-gray-500 h-full flex items-center justify-center">
            No products awaiting approval
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border shadow-sm h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center text-orange-500">
          <AlertCircle className="w-5 h-5 mr-2" />
          Pending Actions
        </CardTitle>
        {pendingProducts.length > 2 && (
          <Link
            to="/seller/products/list"
            className="text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            View All
          </Link>
        )}
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-y-auto">
        <div className="space-y-3 max-h-[148px] overflow-y-auto pr-1">
          {displayedPendingProducts.map((product) => {
            const productImage = product.images?.[0];
            return (
              <div
                key={product.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-yellow-400/20 bg-yellow-50"
              >
                <div className="h-10 w-10 rounded bg-white border border-gray-200 overflow-hidden flex-shrink-0">
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={product.name || "Product"}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">
                    {product.name || "Untitled Product"}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {product.categoryName || "Uncategorized"}
                    {product.createdAt
                      ? ` • ${new Date(product.createdAt).toLocaleDateString()}`
                      : ""}
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-700">
                  Pending
                </Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};